import "./HomeContent.css";
import notificationService from "../../../../services/NotificationService";
import { useState, useEffect, useCallback } from "react";
import Tutorial from "./Tutorial";
import memeService from "../../../../services/MemeService";
import pollingService from "../../../../services/PollingService";
import MostRecentMemesLogic from "./MostRecentMemesLogic";
import LoadingDisplay from "../../../common/LoadingDisplay/LoadingDisplay";
import ManualApproval from "../../../Applications/memeApproval";
import PreloadTemplateService from "../../../../services/PreloadService/PreloadTemplateService.js";
import PreloadSettingsService from "../../../../services/PreloadService/PreloadSettingsService.js";
import eulaDisplayLogic from "../../../../eula/eulaDisplayLogic.js";
import { useTranslation } from "react-i18next";
import useAsync from "../../../../services/useAsync.js";
import TopMenu from "../../Menus/TopMenu/index.js";

const HomeContent = () => {
    const { t } = useTranslation("dashboard");

    const [profanityFilterExist, setProfanityFilterExists] = useState(null);
    const [browserSourceIsActive, setBrowserSourceIsActive] = useState(null);
    const [selectedMemeExists, setSelectedMemeExists] = useState(null);
    const [testMemeExists, setTestMemeExists] = useState(null);
    const [fetchedBrowserSource, setFetchedBrowserSource] = useState("...");
    const [isLoading, setIsLoading] = useState(true);
    const [currentImageDisplay, setCurrentImageDisplay] = useState(0);

    // dependencies for meme approval
    const { loadUserSettings, getSettings, updateUserSettings } =
        PreloadSettingsService();
    const [currentMeme, setCurrentMeme] = useState(null);

    const {
        loadListOfPendingApproval,
        pendingMemes,
        approveOrDenyMeme,
        deletePendingMeme,
        pushPendingMeme,
        loadMostRecentMemes,
        mostRecentMemes,
        deleteMostRecentMeme,
    } = PreloadTemplateService();

    const getUrl = useCallback(async () => {
        const browserSourceResponse = await memeService.getBrowserSource();

        if (browserSourceResponse.url)
            setFetchedBrowserSource(browserSourceResponse.url);
        else {
            let createdBS = await memeService.createBrowserSource();

            if (createdBS.url) setFetchedBrowserSource(createdBS.url);
            else notificationService.setMessage(t("dashboardGetUrlMessage"));
        }
    }, []);

    const loadPage = useCallback(async () => {
        setIsLoading(true);

        eulaDisplayLogic.checkIfTermsOfServcieIsAccepted();

        await Promise.all([
            loadUserSettings(),
            loadMostRecentMemes(),
            getUrl(),
            loadListOfPendingApproval(),
        ]);

        setIsLoading(false);
    }, [
        loadUserSettings,
        loadMostRecentMemes,
        getUrl,
        loadListOfPendingApproval,
    ]);

    useAsync(loadPage);

    useEffect(() => {
        // Update 'currentMeme' with the first meme from 'memesThatNeedApproval' when the array changes.
        // This should be at the top level to ensure proper reactivity.
        setCurrentMeme(pendingMemes[0] || null);
    }, [pendingMemes]);

    pollingService.usePollingInterval(
        async () => {
            const checkSetupProgress = async () => {
                let result = await memeService.getSetupProgress();
                setSelectedMemeExists(
                    result.hasSelectedTemplates
                        ? result.hasSelectedTemplates
                        : false,
                );
                setProfanityFilterExists(
                    result.hasProfanityFilters
                        ? result.hasProfanityFilters
                        : false,
                );
                setTestMemeExists(
                    result.hasCreatedTestMeme
                        ? result.hasCreatedTestMeme
                        : false,
                );
                setBrowserSourceIsActive(
                    result.hasActiveConnection
                        ? result.hasActiveConnection
                        : false,
                );
            };

            checkSetupProgress();
        },
        1000 * 10,
        isLoading,
    );

    let content = <LoadingDisplay />;

    if (!isLoading)
        content = (
            <div className="scrollableContainer">
                <div className="globalContentDiv">
                    <Tutorial
                        profanityFilterExist={profanityFilterExist}
                        selectedMemeExists={selectedMemeExists}
                        testMemeExists={testMemeExists}
                        fetchedBrowserSource={fetchedBrowserSource}
                        browserSourceIsActive={browserSourceIsActive}
                        manualApprovalState={getSettings().manualApproval}
                    />

                    <ManualApproval
                        getSettings={getSettings}
                        updateUserSettings={updateUserSettings}
                        approveOrDenyMeme={approveOrDenyMeme}
                        deletePendingMeme={deletePendingMeme}
                        pushPendingMeme={pushPendingMeme}
                        currentMeme={currentMeme}
                        browserSourceIsActive={browserSourceIsActive}
                        title={t("dashboardPendingMemesTitle")}
                        isDashboard={true}
                    />

                    <MostRecentMemesLogic
                        isLoading={isLoading}
                        mostRecentMemes={mostRecentMemes}
                        currentImageDisplay={currentImageDisplay}
                        setCurrentImageDisplay={setCurrentImageDisplay}
                        deleteMostRecentMeme={deleteMostRecentMeme}
                    />
                </div>
            </div>
        );

    return (
        <>
            <TopMenu />
            {content}
        </>
    );
};

export default HomeContent;
